import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormGroup, Alert } from '@patternfly/react-core';
import { IAction, Td, Tr } from '@patternfly/react-table';

import TextOverrideField from '../../../components/text-override-field';
import { getFormValidationErrorMessage } from '../../../store/global-types/form-errors';
import { defaultValidation, FieldValidationState, validationStatusToPatternFly } from '../../../components/validation-types';
import { getFormErrorsWorstSeverity } from '../../../store/global-types/store-errors';
import { StoreState } from '../../../store/store';
import { CustomizationObjectType } from '../../../store/global-types/customization-types';
import { doseRoiRegExpSet, doseSelectors } from '../../../store/dose/doseSlice';
import { makeSelectFormValidationErrorsForRoi } from '../../../store/dose/doseSelectors';
import { columnCssNames, columnIds } from './DoseRoiTable';
import RegexField from '../../../components/regex-field';
import ValidationHelperText from '../../../components/validation-helper-text';
import IsMandatory from './IsMandatory';
import { ItemActionsColumn } from '../ItemActionsColumn';
import { useTranslation } from 'react-i18next';

interface RoiItemProps {
    roiId: string;
    rowActions?: IAction[];
}

const DoseRoiItem = (props: RoiItemProps) => {
    const { roiId, rowActions } = props;
    
    const { t } = useTranslation();

    const dispatch = useDispatch();

    const roi = useSelector((state: StoreState) => doseSelectors.selectRoiById(state, roiId));
    const originalRoi = useSelector((state: StoreState) => doseSelectors.selectOriginalRoiById(state, roiId));
    const validationError = useSelector((state: StoreState) => doseSelectors.selectCustomizationValidationError(state, roiId));

    const selectFormValidationErrorsForRoi = useMemo(makeSelectFormValidationErrorsForRoi, []);
    const formValidationErrors = useSelector((state: StoreState) => selectFormValidationErrorsForRoi(state, roiId));

    const [nameFieldValidation, setNameFieldValidation] = useState<FieldValidationState>(defaultValidation);

    /** Set non-blocking form validation errors generated in redux store reducers to be visible in this form's normal UI validation. */
    useEffect(() => {
        if (formValidationErrors.length > 0) {
            setNameFieldValidation({ validated: validationStatusToPatternFly(getFormErrorsWorstSeverity(formValidationErrors)), helperTextInvalid: formValidationErrors.map(e => getFormValidationErrorMessage(e.errorType)).join(' ') });
        } else {
            setNameFieldValidation(defaultValidation);
        }
    }, [formValidationErrors, roi]);

    const handleRegExpChange = useCallback((regExp: string) => {
        dispatch(doseRoiRegExpSet({ id: roiId, regExp, }));
    }, [dispatch, roiId]);

    if (roi === undefined) {
        return null;
    }

    const prefix = `roi-${roi.id}`;
    const nameFieldId = `${prefix}-name`;

    const hasValidationError = validationError !== undefined && validationError.type === CustomizationObjectType.Roi;

    return (
        <>
            <Tr key={roiId} className={`roi-item ${hasValidationError ? 'validation-error' : undefined}`}>
                <Td className={columnCssNames.modified} data-testid="oar-is-modified-cell"><div className="roi-item-is-modified" title="This customization has unsaved changes.">{roi.isModified ? '*' : <>&nbsp;</>}</div></Td>

                <Td dataLabel={columnIds.name} className={columnCssNames.name}>
                    <div>
                        <FormGroup
                            fieldId={`field-${nameFieldId}`}
                            className={nameFieldValidation.validated === 'error' ? 'validation-error'
                                : nameFieldValidation.validated === 'warning' ? 'validation-warning' : ''}
                        >
                            <TextOverrideField
                                defaultText={originalRoi ? originalRoi.name : roi.name}
                                textValue={roi.name}
                                id={nameFieldId}
                                allowEmptyField={true}
                                isReadOnly
                            />
                            {nameFieldValidation.validated !== 'default' && (
                                <ValidationHelperText
                                    validated={nameFieldValidation.validated}
                                    helperText={nameFieldValidation.helperTextInvalid}
                                />
                            )}
                        </FormGroup>
                    </div>
                </Td>

                <Td dataLabel={columnIds.isMandatory} className={columnCssNames.isMandatory}>
                    <IsMandatory isMandatory={roi.isMandatory} />
                </Td>

                <Td dataLabel={columnIds.matchPattern} className={columnCssNames.matchPattern} data-testid="oar-match-pattern-cell">
                    <div>
                        <RegexField
                            id={roiId}
                            label={undefined}
                            onChange={handleRegExpChange}
                            regexValue={roi.regExp}
                            alwaysUseRegex
                            enableComposer
                        />
                    </div>
                </Td>

                <Td isActionCell>
                    {rowActions && <ItemActionsColumn items={rowActions} />}
                </Td>
            </Tr>

            {hasValidationError && (
                <Tr className="roi-item validation-error validation-error-box selectable">
                    <Td dataLabel="Validation error" noPadding={true} colSpan={100}>
                        <Alert variant="danger" isInline isPlain isExpandable title={t('common.validationError.message', { error: validationError.message })}>
                            {validationError.field && (<div>Field: {validationError.field}</div>)}
                            <div>Error type: {validationError.detail}</div>
                            <div>Error ctx: {validationError.ctx}</div>
                        </Alert>
                    </Td>
                </Tr>
            )}
        </>
    );
}

export default DoseRoiItem;
